import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Check, Loader2, Send } from 'lucide-react';
import { parseCSVForPreview, getSuggestedIdentifierColumn } from '../../utils/fileUtils';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { formatChatTimestamp } from '../../utils/dateUtils';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  isLoading?: boolean;
  timestamp?: Date;
}

interface CSVConfirmationViewProps {
  file: File;
  tableType: 'companies' | 'people' | 'custom';
  customType: string;
  recordCount: number; // User-selected record count to process from CSV
  onConfirm: (uploadId: string, fileName: string) => void;
  onBack: () => void;
}

type FlowStep = 'parsing' | 'awaiting_selection' | 'uploading';

export const CSVConfirmationView: React.FC<CSVConfirmationViewProps> = ({
  file,
  tableType,
  customType,
  recordCount,
  onConfirm,
  onBack,
}) => {
  const { showToast } = useToast();
  const [step, setStep] = useState<FlowStep>('parsing');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [columns, setColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<Record<string, unknown>[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const hasInitialized = useRef(false);

  const [displayedTitle, setDisplayedTitle] = useState('');
  const [titleComplete, setTitleComplete] = useState(false);

  // Typewriter state for assistant message
  const [typingText, setTypingText] = useState('');
  const [fullText, setFullText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Animate title
  useEffect(() => {
    const targetTitle = `Import ${file.name.replace('.csv', '')}`;
    setDisplayedTitle('');
    setTitleComplete(false);
    let index = 0;

    const timer = setInterval(() => {
      if (index < targetTitle.length) {
        setDisplayedTitle(targetTitle.slice(0, index + 1));
        index++;
      } else {
        setTitleComplete(true);
        clearInterval(timer);
      }
    }, 30);

    return () => clearInterval(timer);
  }, [file.name]);

  // Typewriter effect for assistant message
  useEffect(() => {
    if (!fullText || !isTyping) return;

    let index = 0;
    setTypingText('');

    const timer = setInterval(() => {
      if (index < fullText.length) {
        setTypingText(fullText.slice(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        // Add the completed message to messages array
        setMessages(prev => [...prev, {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: fullText,
          timestamp: new Date()
        }]);
        setTypingText('');
        setFullText('');
        clearInterval(timer);
      }
    }, 15);

    return () => clearInterval(timer);
  }, [fullText, isTyping]);

  // Parse CSV and initialize chat
  const initializeChat = useCallback(async () => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Add user message showing the file
    setMessages([{
      id: 'user-0',
      role: 'user',
      content: `Import ${file.name} as a table`,
      timestamp: new Date()
    }]);

    // Show loading state
    setMessages(prev => [...prev, {
      id: 'loading-1',
      role: 'assistant',
      content: '',
      isLoading: true,
      timestamp: new Date()
    }]);

    // Parse the CSV
    parseCSVForPreview(file, (parsedRowCount, parsedColumns, parsedPreviewData) => {
      setRowCount(parsedRowCount);
      setColumns(parsedColumns);
      setPreviewData(parsedPreviewData);

      const suggestedColumn = getSuggestedIdentifierColumn(parsedColumns);
      setSelectedColumn(suggestedColumn);

      // Remove loading and start typewriter
      setMessages(prev => prev.filter(m => m.id !== 'loading-1'));

      setTimeout(() => {
        const message = `I found ${parsedRowCount.toLocaleString()} rows and ${parsedColumns.length} columns in your file. Which column should I use as the unique identifier to match and enrich your data?`;
        setFullText(message);
        setIsTyping(true);
        setStep('awaiting_selection');
      }, 300);
    }, (error) => {
      console.error('Error parsing CSV:', error);
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== 'loading-1');
        return [...filtered, {
          id: 'assistant-error',
          role: 'assistant',
          content: 'I had trouble reading your CSV file. Please make sure it\'s a valid CSV format and try again.',
          timestamp: new Date()
        }];
      });
    });
  }, [file]);

  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  // Auto-scroll chat
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, step, typingText]);

  // Scroll table to center a specific column
  const scrollToColumn = useCallback((column: string) => {
    const tableContainer = tableScrollRef.current;
    const columnHeader = columnRefs.current.get(column);

    if (tableContainer && columnHeader) {
      // Get the column's position relative to the table container
      const containerWidth = tableContainer.clientWidth;
      const columnLeft = columnHeader.offsetLeft;
      const columnWidth = columnHeader.offsetWidth;

      // Calculate scroll position to center the column in view
      const targetScroll = columnLeft - (containerWidth / 2) + (columnWidth / 2);

      tableContainer.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll to initially suggested column when table appears
  useEffect(() => {
    if (step === 'awaiting_selection' && selectedColumn && !isTyping) {
      // Wait for table to render, then scroll to the suggested column
      const timer = setTimeout(() => scrollToColumn(selectedColumn), 300);
      return () => clearTimeout(timer);
    }
  }, [step, isTyping, selectedColumn, scrollToColumn]);

  const handleColumnSelect = (column: string) => {
    setSelectedColumn(column);

    // Scroll table to show the selected column after a brief delay for render
    // Use multiple attempts to ensure scroll happens after DOM updates
    setTimeout(() => scrollToColumn(column), 50);
    setTimeout(() => scrollToColumn(column), 150);
  };

  const handleConfirm = async () => {
    if (!selectedColumn || isConfirming) return;

    setIsConfirming(true);

    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: `Use "${selectedColumn}" as the identifier`,
      timestamp: new Date()
    }]);

    setTimeout(async () => {
      setStep('uploading');
      setMessages(prev => [...prev, {
        id: `uploading-${Date.now()}`,
        role: 'assistant',
        content: 'Importing your data...',
        isLoading: true,
        timestamp: new Date()
      }]);

      let uploadResult: { import_id: string } | null = null;
      let uploadTitle = '';

      try {
        const identifierIndex = columns.indexOf(selectedColumn);
        const lastDotIndex = file.name.lastIndexOf('.');
        uploadTitle = lastDotIndex > 0 ? file.name.slice(0, lastDotIndex) : file.name;

        uploadResult = await apiClient.uploadDatasetCSV(
          file,
          uploadTitle,
          tableType,
          customType || '',
          recordCount,
          identifierIndex
        );
      } catch (error: any) {
        console.error('Error uploading CSV:', error);
        const errorMessage = error?.response?.data?.detail || error?.message || 'Error uploading CSV. Please try again.';
        showToast(errorMessage, 'error', 5000);

        setMessages(prev => {
          const withoutLoading = prev.filter(m => !m.isLoading);
          return [...withoutLoading, {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `There was an error importing your file: ${errorMessage}`,
            timestamp: new Date()
          }];
        });
        setStep('awaiting_selection');
        setIsConfirming(false);
        return;
      }

      // Upload succeeded - show success and navigate
      // This is outside the try/catch so navigation errors don't show as upload errors
      if (uploadResult) {
        showToast(`Successfully imported ${recordCount.toLocaleString()} rows`, 'success');
        onConfirm(uploadResult.import_id, uploadTitle);
      }
    }, 200);
  };

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isSending) return;

    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date()
    }]);
    setInputValue('');

    // Check if user is confirming
    const confirmPhrases = ['yes', 'yep', 'yeah', 'sure', 'ok', 'okay', 'looks good', 'lgtm', 'correct', 'confirm', 'import', 'do it', 'go'];
    const isConfirmation = confirmPhrases.some(phrase => text.toLowerCase().includes(phrase));

    if (isConfirmation && selectedColumn) {
      handleConfirm();
      return;
    }

    // Check if user is selecting a column by name
    const matchedColumn = columns.find(col =>
      text.toLowerCase().includes(col.toLowerCase()) ||
      col.toLowerCase().includes(text.toLowerCase())
    );

    if (matchedColumn) {
      setSelectedColumn(matchedColumn);
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `I'll use "${matchedColumn}" as the identifier column. Does this look correct?`,
        timestamp: new Date()
      }]);
      return;
    }

    // Generic response
    setIsSending(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: 'Please select a column from the list below to use as the identifier, or type the column name.',
        timestamp: new Date()
      }]);
      setIsSending(false);
    }, 500);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Spreadsheet icon component
  const SpreadsheetIcon = () => (
    <div className="w-10 h-12 bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden flex-shrink-0">
      <div className="h-2.5 bg-emerald-500" />
      <div className="p-1 space-y-0.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex gap-0.5">
            <div className="w-2 h-1 bg-gray-300 rounded-[1px]" />
            <div className="flex-1 h-1 bg-gray-100 rounded-[1px]" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderMessage = (message: ChatMessage) => {
    if (message.role === 'user') {
      return (
        <div key={message.id} className="flex justify-end animate-chatMessageUser group">
          <div className="flex items-center gap-2">
            {message.timestamp && (
              <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {formatChatTimestamp(message.timestamp)}
              </span>
            )}
            <div className="max-w-[80%] bg-gray-900 text-white rounded-2xl rounded-br-md px-4 py-2.5">
              <p className="text-[14px] leading-relaxed">{message.content}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={message.id} className="flex gap-3 animate-chatMessage group">
        <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden mt-0.5">
          <img src={skylarqLogo} alt="Skylarq" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold text-gray-400 tracking-wider uppercase">Skylarq</span>
            {message.timestamp && (
              <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {formatChatTimestamp(message.timestamp)}
              </span>
            )}
          </div>
          {message.isLoading ? (
            <div className="text-gray-500">
              {message.content ? (
                <div className="flex items-center gap-2.5 text-[14px] text-gray-600">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span>{message.content}</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '300ms' }} />
                </div>
              )}
            </div>
          ) : (
            <p className="text-[15px] text-gray-700 leading-relaxed">{message.content}</p>
          )}
        </div>
      </div>
    );
  };

  // Render the typing message with typewriter effect
  const renderTypingMessage = () => {
    if (!isTyping || !typingText) return null;

    return (
      <div className="flex gap-3 animate-chatMessage group">
        <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden mt-0.5">
          <img src={skylarqLogo} alt="Skylarq" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold text-gray-400 tracking-wider uppercase">Skylarq</span>
          </div>
          <p className="text-[15px] text-gray-700 leading-relaxed">
            {typingText}
            <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse" />
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden h-screen-safe">
      {/* Header - Fixed height, mobile optimized */}
      <div className="flex-shrink-0">
        <div className="h-12 sm:h-14 flex items-center justify-between px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={onBack}
              disabled={step === 'uploading'}
              className="p-1.5 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-[14px] sm:text-[16px] font-semibold text-gray-900 truncate">
              {displayedTitle}
              {!titleComplete && <span className="animate-pulse">|</span>}
            </h1>
          </div>
        </div>
        <div className="border-b border-gray-200" />
      </div>

      {/* Chat Area - Scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">
        <div className="max-w-2xl mx-auto px-3 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-5">
          {messages.map(renderMessage)}

          {/* Typewriter message */}
          {renderTypingMessage()}

          {/* File Card + Column Selector - shown after parsing complete */}
          {step === 'awaiting_selection' && columns.length > 0 && !isTyping && (
            <div className="ml-8 sm:ml-10 space-y-3 sm:space-y-4">
              {/* File Info Card - Compact on mobile */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <SpreadsheetIcon />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] sm:text-[14px] font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-[11px] sm:text-[12px] text-gray-500 mt-0.5">
                    {rowCount.toLocaleString()} rows • {columns.length} cols
                  </p>
                </div>
              </div>

              {/* Column Selector - Scrollable on mobile */}
              <div>
                <p className="text-[11px] sm:text-[12px] text-gray-500 mb-2">Tap to select identifier:</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 max-h-[100px] sm:max-h-[150px] overflow-y-auto pb-1">
                  {columns.map((column, index) => (
                    <button
                      key={index}
                      onClick={() => handleColumnSelect(column)}
                      className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-[12px] sm:text-[13px] font-medium rounded-lg border transition-all active:scale-95 ${
                        selectedColumn === column
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 active:bg-gray-50'
                      }`}
                    >
                      {column.length > 18 ? column.slice(0, 18) + '...' : column}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data Preview Table - Horizontal scroll, limited rows on mobile */}
              {previewData.length > 0 && selectedColumn && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] sm:text-[12px] text-gray-500">Preview</p>
                    <span className="text-[10px] sm:text-[11px] text-gray-400 bg-gray-100 px-1.5 sm:px-2 py-0.5 rounded">
                      {Math.min(3, previewData.length)} of {rowCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="rounded-xl border border-gray-200 overflow-hidden -mx-1 sm:mx-0">
                    <div ref={tableScrollRef} className="overflow-x-auto scroll-smooth">
                      <table className="w-full text-[11px] sm:text-[13px]">
                        <thead className="sticky top-0 z-10">
                          <tr>
                            {columns.map((column, index) => {
                              const isIdentifier = selectedColumn === column;
                              return (
                                <th
                                  key={index}
                                  ref={(el) => {
                                    if (el) columnRefs.current.set(column, el);
                                  }}
                                  className={`px-2 sm:px-3 py-2 sm:py-2.5 text-left text-[9px] sm:text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap border-b transition-colors duration-200 ${
                                    isIdentifier
                                      ? 'bg-gray-900 text-white'
                                      : 'bg-gray-50 text-gray-600 border-gray-200'
                                  }`}
                                  style={{ minWidth: '80px' }}
                                >
                                  <div className="flex items-center gap-1">
                                    <span>{column.length > 12 ? column.slice(0, 12) + '...' : column}</span>
                                    {isIdentifier && <Check className="w-2.5 sm:w-3 h-2.5 sm:h-3" />}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {previewData.slice(0, 3).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {columns.map((column, colIndex) => {
                                const isIdentifier = selectedColumn === column;
                                const cellValue = row[column];
                                const displayValue = cellValue != null && cellValue !== ''
                                  ? String(cellValue).slice(0, 20) + (String(cellValue).length > 20 ? '...' : '')
                                  : '—';
                                return (
                                  <td
                                    key={colIndex}
                                    className={`px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap transition-colors duration-200 ${
                                      isIdentifier ? 'font-medium text-gray-900 bg-gray-50' : 'text-gray-600'
                                    }`}
                                  >
                                    {displayValue}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm Button - Full width on mobile */}
              {selectedColumn && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
                  <button
                    onClick={handleConfirm}
                    disabled={isConfirming}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 text-[13px] font-medium text-white bg-gray-900 rounded-xl sm:rounded-full hover:bg-gray-800 active:bg-gray-950 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    <span>Import with "{selectedColumn.length > 15 ? selectedColumn.slice(0, 15) + '...' : selectedColumn}"</span>
                  </button>
                  <button
                    onClick={() => inputRef.current?.focus()}
                    className="px-4 py-2.5 sm:py-2 text-[13px] text-gray-600 hover:text-gray-800 active:bg-gray-100 rounded-xl sm:rounded-full border border-gray-200 transition-colors"
                  >
                    Change column
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Input Area - Fixed at bottom with safe area */}
      <div className="flex-shrink-0 px-3 sm:px-6 pb-safe-4 pt-2 bg-white border-t border-gray-100">
        <div className="max-w-2xl mx-auto">
          {step === 'awaiting_selection' && !isTyping ? (
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message or confirm..."
                disabled={isSending}
                className="w-full px-4 py-3 pr-12 text-[14px] bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300 focus:bg-white focus:shadow-sm transition-all disabled:opacity-50"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isSending}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors"
              >
                {isSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          ) : step === 'uploading' ? (
            <div className="flex items-center justify-center py-4 text-[13px] text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                <span>Importing your data...</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
